import { alpha, Box, Theme, ToggleButton, Typography } from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import React from 'react';

import { DATA_TEST, getDynamicDataTestId } from '../NavBar/enums';

const getFilterButtonStyle = (theme: Theme) => ({
  px: 1,
  py: '1px',
  fontSize: capitalDesignTokens.foundations.typography.fontSize.bodyXSmall,
  lineHeight: capitalDesignTokens.foundations.typography.lineHeight.bodyXSmall,
  fontWeight: capitalDesignTokens.foundations.typography.fontWeight.semiBold,
  color: 'text.disabled',
  bgcolor: 'action.hover',
  gap: 0.5,
  '& svg': {
    maxWidth: 14,
    maxHeight: 14,
    fill: theme.palette.text.secondary
  },
  '&.Mui-selected': {
    borderColor: 'primary.main',
    bgcolor: alpha(theme.palette.primary.main, 0.12),
    color: 'primary.main',
    cursor: 'default',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.12)
    }
  }
});

export type FilterLabel = 'Filter by' | 'Show me';

export type FilterOptions = {
  value: string;
  label: string;
  Icon?: React.ElementType;
};

/**
 * Props for the SearchFilters component that renders a row of toggle buttons
 * for filtering search results.
 */
export type SearchFiltersProps = {
  /** The currently active filter value. */
  activeFilter: string;
  /** The list of filter options to display as toggle buttons. */
  filterOptions: FilterOptions[];
  /** The label displayed before the filter buttons. */
  filterLabel?: FilterLabel;
  /** Callback invoked when a filter button is selected. */
  onFilterChange(documentTypeFilter: string): void;
  /** Controls how overflowing filter buttons are handled. `'wrap'` wraps buttons to the next line; `'scroll'` enables horizontal scrolling. */
  overflow?: 'scroll' | 'wrap';
};

export const SearchFilters = ({
  activeFilter,
  filterOptions,
  filterLabel = 'Show me',
  onFilterChange,
  overflow = 'wrap'
}: SearchFiltersProps) => {
  return (
    <Box
      data-test="searchFilters"
      display="flex"
      alignItems="center"
      gap={1.5}
      px={1}
      style={
        overflow === 'scroll'
          ? { overflowX: 'auto', whiteSpace: 'nowrap', flexWrap: 'nowrap' }
          : { flexWrap: 'wrap' }
      }
    >
      <Typography variant="h6" component="span">
        {filterLabel}:
      </Typography>
      {filterOptions.map(({ label, value, Icon }) => (
        <ToggleButton
          sx={(theme) => getFilterButtonStyle(theme)}
          key={value}
          value={value}
          selected={value === activeFilter}
          color="secondary"
          size="small"
          onClick={() => onFilterChange(value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onFilterChange(value);
            }
          }}
          data-test={getDynamicDataTestId(DATA_TEST.global_search_filter_button, value)}
        >
          {Icon && <Icon />}
          {label}
        </ToggleButton>
      ))}
    </Box>
  );
};
